import {makeAutoObservable} from 'mobx';
import LoadingModule from '../../LoadingModule/LoadingModule';
import {AIRepository} from '../../../data/repositories/AiRepository';
import {toast} from 'react-toastify';
import {getErrorFromCode} from '../../../data/errorCodes';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import {aiAssitantChatbotText} from '../aiAssistantChatbotText';
import OrganizationRepository from '../../../data/repositories/OrganizationRepository';
import {Organization} from '../../../data/models/Oganization/Organization';

class AiAssistantChatbotStore {
  private aiRepo = new AIRepository();
  private aiAssistantId?: string;
  private organizationRepo = new OrganizationRepository();

  private loadingModule = new LoadingModule();
  private sendingMessageLoading = new LoadingModule();

  constructor() {
    makeAutoObservable(this);
  }

  public get currentAssistant() {
    return this.aiAssistantId;
  }

  public get shouldShowChatbot() {
    return this.aiAssistantId !== undefined;
  }

  public get isSendingMessage() {
    return this.sendingMessageLoading.isLoading;
  }

  public showChatbot(aiAssistantId: string) {
    this.aiAssistantId = aiAssistantId;
  }

  public closeChatbot() {
    this.aiAssistantId = undefined;
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  public async getAssistantChat(organizationId: string, assistantId: string) {
    if (!assistantId) return;
    this.loadingModule.startLoading();
    const result = await this.aiRepo.getAssistantChat(
      organizationId,
      assistantId,
    );
    this.loadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
  }

  public async getAssistantPublicChat(
    organizationId: string,
    assistantId: string,
    tabId: string,
  ) {
    if (!assistantId) return;
    this.loadingModule.startLoading();
    const result = await this.aiRepo.getAssistantPublicChat(
      organizationId,
      assistantId,
      tabId,
    );
    this.loadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
  }

  public async clearAssistantChat(tabId: string) {
    if (!this.aiAssistantId) return;
    this.loadingModule.startLoading();
    const result =
      tabId === ''
        ? await this.aiRepo.clearAiAssistantChat(this.aiAssistantId)
        : await this.aiRepo.clearAiAssistantPublicChat(
            this.aiAssistantId,
            tabId,
          );
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  public async sendMessageStream(
    organizationId: string,
    tabId: string,
    message: string,
    onChunk: (chunk: string) => void,
    onComplete: () => void,
    learnerView?: boolean,
  ) {
    if (!this.aiAssistantId) return;
    this.sendingMessageLoading.startLoading();

    const onError = (error: string) => {
      toast.error(
        learnerView
          ? aiAssitantChatbotText.noCreditsForLearner
          : getErrorFromCode(error),
        toastConfig,
      );
    };

    try {
      if (tabId === '') {
        await this.aiRepo.sendAssistantChatStream(
          organizationId,
          this.aiAssistantId,
          message,
          onChunk,
          onError,
        );
      } else {
        await this.aiRepo.sendAssistantPublicChatStream(
          organizationId,
          this.aiAssistantId,
          tabId,
          message,
          onChunk,
          onError,
        );
      }
      onComplete();
    } finally {
      this.sendingMessageLoading.endLoading();
    }
  }

  public async getMyOrganizationDetails() {
    const result = await this.organizationRepo.getUserOrganization();
    if (result.success && result.data) {
      return result.data as Organization;
    } else {
      if (result.errors && result.errors.length) {
        toast.error(getErrorFromCode(result.errors[0]), toastConfig);
      }
      return undefined;
    }
  }

  public async setColorsFromQueryString(
    primaryColor: string,
    secondaryColor: string,
  ) {
    if (primaryColor && secondaryColor) {
      this.organizationRepo.setThemeColors(primaryColor, secondaryColor);
    }
  }
}

export default AiAssistantChatbotStore;
